import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/table-questions — rebuilt in brand primitives. Diner Q&A by
 * dietary restriction. Questions use templates that get filled with
 * {restaurant} + {restriction}. Optimistic UI (ask/answer) until the
 * backend endpoints ship.
 */

const TEMPLATES = [
  { id: 'can_eat_safely',     label: 'Can eat safely?',      category: 'Safety',          template: 'Has anyone with {restriction} eaten safely at {restaurant}?' },
  { id: 'what_worked',        label: 'What worked?',         category: 'Recommendations', template: 'What did you order that worked for {restriction} at {restaurant}?' },
  { id: 'kitchen_understands',label: 'Kitchen understanding',category: 'Safety',          template: 'Did the kitchen at {restaurant} understand cross-contact for {restriction}?' },
  { id: 'has_allergen_binder',label: 'Allergen binder?',     category: 'Information',     template: 'Does {restaurant} have an allergen binder or ingredient list?' },
  { id: 'change_gloves',      label: 'Changed gloves?',      category: 'Safety',          template: 'Did staff at {restaurant} change gloves when preparing for {restriction}?' },
  { id: 'trust_again',        label: 'Trust again?',         category: 'Trust',           template: 'Would you trust {restaurant} again for {restriction}?' },
];

const STATUS_META = {
  open:     { label: 'Open',     bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  dot: 'var(--cwm-blue)' },
  answered: { label: 'Answered', bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', dot: 'var(--cwm-green)' },
  expired:  { label: 'Expired',  bg: 'var(--cwm-gray-100)',    fg: 'var(--cwm-gray-500)',   dot: 'var(--cwm-gray-400)' },
};

const FILTER_TABS = [
  { id: 'all',      label: 'All' },
  { id: 'open',     label: 'Open' },
  { id: 'answered', label: 'Answered' },
  { id: 'expired',  label: 'Expired' },
];

const COMMON_RESTRICTIONS = [
  'peanut allergy', 'tree nut allergy', 'shellfish allergy', 'gluten / celiac',
  'dairy / lactose', 'soy allergy', 'egg allergy', 'vegan',
];

export default function UnifiedTableQuestions() {
  const navigate = useNavigate();
  const { userId, isInitialized } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [askOpen, setAskOpen] = useState(false);
  const [answerFor, setAnswerFor] = useState(null); // question being answered
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [list, live] = await Promise.all([
          dataService.listRestaurants(),
          // Real questions from the backend. If it errors or comes
          // back empty we fall back to DEMO_QUESTIONS so the filter
          // tabs + empty states still have something to reason over
          // during design review.
          dataService.listTableQuestions(userId).catch(() => null),
        ]);
        if (cancelled) return;
        setRestaurants(Array.isArray(list) ? list : (list?.restaurants || []));
        setQuestions(Array.isArray(live) && live.length > 0 ? live : DEMO_QUESTIONS);
      } catch {
        if (!cancelled) { setRestaurants([]); setQuestions(DEMO_QUESTIONS); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isInitialized, navigate]);

  const filtered = useMemo(() => {
    if (filter === 'all') return questions;
    return questions.filter((q) => q.status === filter);
  }, [questions, filter]);

  const counts = useMemo(() => ({
    all: questions.length,
    open: questions.filter((q) => q.status === 'open').length,
    answered: questions.filter((q) => q.status === 'answered').length,
    expired: questions.filter((q) => q.status === 'expired').length,
  }), [questions]);

  const submitQuestion = async (newQ) => {
    // `newQ` is the shape the AskQuestionDialog builds — see line 349 in
    // this file. We reuse its fields and post to /api/tableQuestion.
    // The backend returns the hydrated row (with real db id + status);
    // if the POST fails we fall back to the local optimistic row so
    // the UI stays responsive.
    let saved = null;
    try {
      saved = await dataService.createTableQuestion({
        user_id: userId,
        restaurant_id: newQ.restaurant_id,
        template_id: newQ.template_id,
        question_text: newQ.question_text,
        dietary_restriction: newQ.dietary_restriction,
      });
    } catch {}
    setQuestions((prev) => [saved || newQ, ...prev]);
    setAskOpen(false);
    setAlert({ tone: 'green', text: 'Question sent to the table.' });
    setTimeout(() => setAlert(null), 2800);
  };

  const submitAnswer = async (questionId, answer) => {
    // Optimistic local update first, then POST. If the POST fails the
    // optimistic row stays (worst case: other diners don't see it yet).
    // If it succeeds the backend returns the fully-hydrated question,
    // which we swap in so answer IDs + timestamps come from the server.
    setQuestions((prev) => prev.map((q) => q.id === questionId
      ? { ...q, status: 'answered', answers: [...(q.answers || []), answer] }
      : q
    ));
    setExpanded((prev) => ({ ...prev, [questionId]: true }));
    setAnswerFor(null);
    setAlert({ tone: 'green', text: 'Thanks for answering — the table will see it.' });
    setTimeout(() => setAlert(null), 2800);
    try {
      // Demo-seeded questions don't have a real db_id — skip the network
      // for those so we don't 404 against a non-existent row.
      const target = questions.find((q) => q.id === questionId);
      if (!target?.db_id) return;
      const saved = await dataService.answerTableQuestion(target.db_id, {
        user_id: userId,
        answer_text: answer.answer_text,
        what_ordered: answer.what_ordered,
      });
      setQuestions((prev) => prev.map((q) => q.id === questionId ? saved : q));
    } catch {}
  };

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 940, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
          style={{ textAlign: 'center', marginBottom: 24 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <ChatIcon size={12} /> TABLE QUESTIONS
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
            Ask the table, <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>answer the table</em>.
          </h1>
          <p style={{ maxWidth: 560, margin: '0 auto', fontSize: 15, fontWeight: 500, color: 'var(--cwm-gray-700)' }}>
            Ask specific questions about a restaurant for a specific dietary restriction. Diners who've been there can answer.
          </p>
          <button className="cwm-btn" style={{ marginTop: 20 }} onClick={() => setAskOpen(true)}>
            <PlusIcon size={14} /> Ask a question
          </button>
        </motion.section>

        {/* Alert */}
        <AnimatePresence>
          {alert && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              style={{
                padding: '10px 14px', borderRadius: 14, marginBottom: 16,
                background: alert.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
                color: alert.tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
                fontSize: 13, fontWeight: 800,
              }}
            >
              {alert.text}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filter tabs */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18, justifyContent: 'center' }}>
          {FILTER_TABS.map((t) => (
            <FilterPill key={t.id} label={t.label} count={counts[t.id]} active={filter === t.id} onClick={() => setFilter(t.id)} />
          ))}
        </div>

        {/* Questions */}
        {loading ? (
          <QuestionSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState filter={filter} onAsk={() => setAskOpen(true)} />
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {filtered.map((q, i) => (
              <QuestionCard
                key={q.id} question={q} index={i}
                expanded={!!expanded[q.id]}
                onToggle={() => setExpanded((p) => ({ ...p, [q.id]: !p[q.id] }))}
                onAnswer={() => setAnswerFor(q)}
              />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {askOpen && (
          <AskQuestionDialog
            restaurants={restaurants}
            onClose={() => setAskOpen(false)}
            onSubmit={submitQuestion}
          />
        )}
        {answerFor && (
          <AnswerDialog
            question={answerFor}
            onClose={() => setAnswerFor(null)}
            onSubmit={(a) => submitAnswer(answerFor.id, a)}
          />
        )}
      </AnimatePresence>
    </AppShell>
  );
}

// ---------- sub-components ----------

function FilterPill({ label, count, active, onClick }) {
  return (
    <motion.button
      type="button" onClick={onClick}
      whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '8px 18px', borderRadius: 999,
        background: active ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : '#fff',
        color: active ? '#fff' : 'var(--cwm-blue-deep)',
        border: `1.5px solid ${active ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
        cursor: 'pointer',
        fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 900,
        boxShadow: active ? '0 6px 16px rgba(15,127,232,0.32)' : 'none',
      }}
    >
      {label}
      <span style={{
        padding: '1px 8px', borderRadius: 999,
        background: active ? 'rgba(255,255,255,0.24)' : 'var(--cwm-blue-pale)',
        color: active ? '#fff' : 'var(--cwm-blue-deep)',
        fontSize: 11, fontWeight: 900,
      }}>{count}</span>
    </motion.button>
  );
}

function QuestionCard({ question, index, expanded, onToggle, onAnswer }) {
  const status = STATUS_META[question.status] || STATUS_META.open;
  const answerCount = (question.answers || []).length;
  const date = new Date(question.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.34, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      className="cwm-sheet"
      style={{ padding: 18 }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)' }}>
          {question.restaurant_name}
        </div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 999,
          background: status.bg, color: status.fg,
          fontSize: 11, fontWeight: 900, letterSpacing: '0.04em',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: status.dot }}/>
          {status.label}
        </span>
      </header>

      <h3 style={{ fontSize: 16, fontWeight: 900, color: 'var(--cwm-ink)', margin: '0 0 10px', lineHeight: 1.35, letterSpacing: '-0.005em' }}>
        {question.question_text}
      </h3>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, alignItems: 'center' }}>
        <span style={{
          padding: '3px 9px', borderRadius: 999,
          background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)',
          fontSize: 11, fontWeight: 800,
        }}>
          {question.dietary_restriction}
        </span>
        <span style={{ fontSize: 12, color: 'var(--cwm-gray-500)', fontWeight: 700 }}>
          Asked by {question.is_own ? 'you' : 'a table member'} · {date}
        </span>
      </div>

      <AnimatePresence>
        {expanded && answerCount > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ padding: '10px 12px 0', borderTop: '1px solid var(--cwm-gray-100)', marginTop: 4 }}>
              {question.answers.map((a, i) => (
                <div key={i} style={{ padding: '10px 0', borderBottom: i < answerCount - 1 ? '1px solid var(--cwm-gray-100)' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 900 }}>
                        {(a.answerer_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--cwm-ink)' }}>{a.answerer_name || 'Table member'}</span>
                    </div>
                    {a.what_ordered && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
                        ordered: <strong style={{ color: 'var(--cwm-ink)' }}>{a.what_ordered}</strong>
                      </span>
                    )}
                  </div>
                  <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--cwm-gray-700)', fontWeight: 500 }}>
                    {a.answer_text}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: answerCount > 0 && !expanded ? '1px solid var(--cwm-gray-100)' : 'none' }}>
        {answerCount > 0 ? (
          <button type="button" onClick={onToggle}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'transparent', border: 'none',
                    color: 'var(--cwm-blue-deep)', cursor: 'pointer',
                    fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
                  }}>
            {expanded ? 'Hide' : 'Show'} {answerCount} answer{answerCount === 1 ? '' : 's'}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 200ms ease' }}>
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>No answers yet</span>
        )}
        {question.status !== 'expired' && !question.is_own && (
          <button type="button" onClick={onAnswer}
                  style={{
                    padding: '6px 14px', borderRadius: 999,
                    background: 'linear-gradient(90deg, #1E9BFF, #0F7FE8)', color: '#fff',
                    border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                    boxShadow: '0 4px 10px rgba(15,127,232,0.28)',
                  }}>
            Answer
          </button>
        )}
      </footer>
    </motion.article>
  );
}

function AskQuestionDialog({ restaurants, onClose, onSubmit }) {
  const [templateId, setTemplateId] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const [restriction, setRestriction] = useState('');
  const [error, setError] = useState('');

  const template = TEMPLATES.find((t) => t.id === templateId);
  const restaurant = restaurants.find((r) => String(r.id) === String(restaurantId));
  const preview = template && restaurant && restriction
    ? template.template.replace('{restaurant}', restaurant.name).replace('{restriction}', restriction)
    : null;

  const submit = () => {
    if (!templateId) return setError('Pick a template.');
    if (!restaurantId) return setError('Pick a restaurant.');
    if (!restriction.trim()) return setError('Add a dietary restriction.');
    onSubmit({
      id: Date.now(),
      template_id: templateId,
      question_text: preview,
      restaurant_id: restaurantId,
      restaurant_name: restaurant.name,
      dietary_restriction: restriction.trim(),
      is_own: true,
      asker_name: 'You',
      status: 'open',
      created_at: new Date().toISOString(),
      answers: [],
    });
  };

  return (
    <Modal onClose={onClose}>
      <header style={{ marginBottom: 18 }}>
        <div className="cwm-eyebrow-badge" style={{ marginBottom: 8 }}>
          <ChatIcon size={12} /> ASK A QUESTION
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 900, margin: 0, letterSpacing: '-0.01em', color: 'var(--cwm-ink)' }}>
          What do you want to know?
        </h2>
      </header>

      <Field label="Template">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {TEMPLATES.map((t) => {
            const on = templateId === t.id;
            return (
              <button key={t.id} type="button" onClick={() => setTemplateId(t.id)}
                      style={{
                        padding: '10px 12px', borderRadius: 14,
                        background: on ? 'linear-gradient(180deg, #fff, var(--cwm-blue-pale))' : '#fff',
                        border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
                        fontFamily: 'var(--cwm-font)', textAlign: 'left', cursor: 'pointer',
                        boxShadow: on ? '0 6px 14px rgba(15,127,232,0.18)' : 'none',
                        transition: 'all 140ms ease',
                      }}>
                <div style={{ fontSize: 12.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>{t.label}</div>
                <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--cwm-gray-500)', letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: 2 }}>
                  {t.category}
                </div>
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Restaurant">
        <SelectPill value={restaurantId} onChange={setRestaurantId}
                    options={[{ value: '', label: 'Select a restaurant…' }, ...restaurants.slice(0, 50).map((r) => ({ value: String(r.id), label: r.name }))]} />
      </Field>

      <Field label="Dietary restriction">
        <input
          value={restriction}
          onChange={(e) => setRestriction(e.target.value)}
          placeholder="e.g. peanut allergy"
          list="cwm-restrictions"
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '12px 16px', borderRadius: 14,
            border: '1.5px solid var(--cwm-blue-light)', background: '#F7FBFF',
            color: 'var(--cwm-ink)', fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
            outline: 'none',
          }}
        />
        <datalist id="cwm-restrictions">
          {COMMON_RESTRICTIONS.map((r) => <option key={r} value={r}/>)}
        </datalist>
      </Field>

      {preview && (
        <div style={{ padding: '12px 14px', borderRadius: 14, background: 'var(--cwm-blue-pale)', border: '1px solid var(--cwm-blue-light)', marginBottom: 10 }}>
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)', marginBottom: 4 }}>PREVIEW</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--cwm-ink)' }}>{preview}</div>
        </div>
      )}

      {error && <div style={{ fontSize: 13, color: 'var(--cwm-red-text)', fontWeight: 700, marginBottom: 10 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
        <button className="cwm-btn-secondary" onClick={onClose}>Cancel</button>
        <button className="cwm-btn" onClick={submit}>Ask the table</button>
      </div>
    </Modal>
  );
}

function AnswerDialog({ question, onClose, onSubmit }) {
  const [text, setText] = useState('');
  const [ordered, setOrdered] = useState('');
  const [error, setError] = useState('');

  const submit = () => {
    if (!text.trim()) return setError('Share what happened.');
    onSubmit({
      id: Date.now(),
      answer_text: text.trim(),
      what_ordered: ordered.trim() || null,
      answerer_name: 'You',
      created_at: new Date().toISOString(),
    });
  };

  return (
    <Modal onClose={onClose}>
      <header style={{ marginBottom: 16 }}>
        <div className="cwm-eyebrow-badge" style={{ marginBottom: 8 }}>
          ANSWER QUESTION
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 900, margin: '0 0 6px', color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
          {question.question_text}
        </h2>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
          at {question.restaurant_name} · {question.dietary_restriction}
        </div>
      </header>

      <Field label="What happened?">
        <textarea
          value={text} onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder="Share your experience — what worked, what didn't."
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '12px 16px', borderRadius: 14,
            border: '1.5px solid var(--cwm-blue-light)', background: '#F7FBFF',
            color: 'var(--cwm-ink)', fontFamily: 'var(--cwm-font)',
            fontSize: 14, fontWeight: 500, lineHeight: 1.5,
            outline: 'none', resize: 'vertical',
          }}
        />
      </Field>

      <Field label="What did you order? (optional)">
        <input
          value={ordered} onChange={(e) => setOrdered(e.target.value)}
          placeholder="e.g. gluten-free pappardelle"
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '12px 16px', borderRadius: 14,
            border: '1.5px solid var(--cwm-blue-light)', background: '#F7FBFF',
            color: 'var(--cwm-ink)', fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
            outline: 'none',
          }}
        />
      </Field>

      {error && <div style={{ fontSize: 13, color: 'var(--cwm-red-text)', fontWeight: 700, marginBottom: 10 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
        <button className="cwm-btn-secondary" onClick={onClose}>Cancel</button>
        <button className="cwm-btn" onClick={submit}>Share answer</button>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10,37,64,0.55)', backdropFilter: 'blur(6px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <motion.div
        initial={{ scale: 0.96, y: 10, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, y: 10, opacity: 0 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 28, padding: 26, maxWidth: 540, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--cwm-shadow-hero)' }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-700)' }}>{label}</div>
      {children}
    </div>
  );
}

function SelectPill({ value, onChange, options }) {
  return (
    <label style={{ position: 'relative', display: 'block' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', boxSizing: 'border-box',
          appearance: 'none',
          padding: '12px 36px 12px 16px',
          borderRadius: 14,
          border: '1.5px solid var(--cwm-blue-light)',
          background: '#F7FBFF',
          color: 'var(--cwm-ink)',
          fontFamily: 'var(--cwm-font)',
          fontSize: 14, fontWeight: 600,
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
           style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--cwm-blue-deep)', pointerEvents: 'none' }}>
        <path d="M6 9l6 6 6-6"/>
      </svg>
    </label>
  );
}

function QuestionSkeleton() {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} className="cwm-sheet" style={{ padding: 18, opacity: 0.6 }}>
          <div style={{ height: 12, width: 120, background: 'var(--cwm-gray-100)', borderRadius: 6, marginBottom: 10 }}/>
          <div style={{ height: 18, width: '80%', background: 'var(--cwm-gray-100)', borderRadius: 8, marginBottom: 12 }}/>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ height: 20, width: 100, background: 'var(--cwm-gray-100)', borderRadius: 999 }}/>
            <div style={{ height: 20, width: 140, background: 'var(--cwm-gray-100)', borderRadius: 999 }}/>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ filter, onAsk }) {
  const msg = filter === 'all' ? 'No questions yet.' : `Nothing in "${filter}".`;
  return (
    <div className="cwm-sheet" style={{ padding: '44px 24px', textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, margin: '0 auto 14px', borderRadius: '50%', background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)', display: 'grid', placeItems: 'center' }}>
        <ChatIcon size={28} />
      </div>
      <h3 style={{ fontSize: 20, fontWeight: 900, margin: '0 0 8px', color: 'var(--cwm-ink)' }}>{msg}</h3>
      <p style={{ maxWidth: 380, margin: '0 auto 18px', fontSize: 14, color: 'var(--cwm-gray-500)', fontWeight: 500 }}>
        Ask about a restaurant for a specific dietary restriction — diners who've been there will answer.
      </p>
      <button className="cwm-btn" onClick={onAsk}>
        <PlusIcon size={14} /> Ask a question
      </button>
    </div>
  );
}

// ---------- icons ----------
function ChatIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 01-4.5 7.8L17 22l-4-2a9 9 0 119-8z"/></svg>; }
function PlusIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }

// ---------- demo data ----------
const DEMO_QUESTIONS = [
  {
    id: 1,
    template_id: 'can_eat_safely',
    question_text: 'Has anyone with peanut allergy eaten safely at Il Violino?',
    restaurant_id: 0, restaurant_name: 'Il Violino',
    dietary_restriction: 'peanut allergy',
    is_own: false, asker_name: 'Table member',
    status: 'answered',
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    answers: [
      { id: 101, answer_text: 'Yes — I\'ve eaten there three times with a peanut allergy and had no reactions. Kitchen is very careful.', answerer_name: 'Sarah M.', what_ordered: 'Gluten-Free Pappardelle' },
      { id: 102, answer_text: 'Manager came out personally to confirm cross-contact protocol before I ordered.', answerer_name: 'John D.', what_ordered: 'Osso Buco' },
    ],
  },
  {
    id: 2,
    template_id: 'what_worked',
    question_text: 'What did you order that worked for gluten-free at Koji Ramen?',
    restaurant_id: 1, restaurant_name: 'Koji Ramen',
    dietary_restriction: 'gluten-free',
    is_own: true, asker_name: 'You',
    status: 'open',
    created_at: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    answers: [],
  },
  {
    id: 3,
    template_id: 'kitchen_understands',
    question_text: 'Did the kitchen at Maya Mezze understand cross-contact for tree nut allergy?',
    restaurant_id: 2, restaurant_name: 'Maya Mezze',
    dietary_restriction: 'tree nut allergy',
    is_own: false, asker_name: 'Table member',
    status: 'answered',
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    answers: [
      { id: 301, answer_text: 'Yes — they have a dedicated prep area for nut-free orders. Confirmed by the chef.', answerer_name: 'Alex T.', what_ordered: 'Grilled Halloumi + Greek Salad' },
    ],
  },
  {
    id: 4,
    template_id: 'trust_again',
    question_text: 'Would you trust Little Beet Table again for shellfish allergy?',
    restaurant_id: 3, restaurant_name: 'Little Beet Table',
    dietary_restriction: 'shellfish allergy',
    is_own: false, asker_name: 'Table member',
    status: 'expired',
    created_at: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
    answers: [],
  },
];
